import {useEffect} from 'react'
import {getDocumentScrollOwner} from './documentEditorOwnership'

const SCROLL_LOCK_STATE_KEY = '__ambersportScrollLockState'
const NO_SCROLL_CLASS = 'no-scroll'

type ScrollLockState = {
    count: number
    scrollY: number
    scrollOwner: HTMLElement | Document | null
    originalPaddingRight: string
    htmlHadNoScroll: boolean
    bodyHadNoScroll: boolean
}

const getScrollLockState = (): ScrollLockState => {
    const win = window as unknown as Record<string, ScrollLockState | undefined>
    return win[SCROLL_LOCK_STATE_KEY] ??= {
        count: 0,
        scrollY: 0,
        scrollOwner: null,
        originalPaddingRight: '',
        htmlHadNoScroll: false,
        bodyHadNoScroll: false,
    }
}

export type ScrollLockChange = {lockAction: 'locked' | 'released'; lockCount: number; scrollLockStateConsistent?: boolean}

interface UseScrollLockOptions {
    enabled?: boolean
    onChange?: (state: ScrollLockChange) => void
}

export function useDocumentScrollLock({enabled = true, onChange}: UseScrollLockOptions = {}) {
    useEffect(() => {
        if (!enabled) return

        const body = document.body
        const documentElement = document.documentElement
        const state = getScrollLockState()

        if (state.count === 0) {
            state.scrollY = window.scrollY || documentElement.scrollTop
            state.scrollOwner = getDocumentScrollOwner()
            state.originalPaddingRight = body.style.paddingRight
            state.htmlHadNoScroll = documentElement.classList.contains(NO_SCROLL_CLASS)
            state.bodyHadNoScroll = body.classList.contains(NO_SCROLL_CLASS)

            documentElement.classList.add(NO_SCROLL_CLASS)
            body.classList.add(NO_SCROLL_CLASS)

            const scrollbarWidth = window.innerWidth - documentElement.clientWidth
            if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
        }

        state.count += 1
        onChange?.({lockAction: 'locked', lockCount: state.count})

        return () => {
            const currentState = getScrollLockState()
            currentState.count = Math.max(0, currentState.count - 1)

            if (currentState.count === 0) {
                if (!currentState.htmlHadNoScroll) documentElement.classList.remove(NO_SCROLL_CLASS)
                if (!currentState.bodyHadNoScroll) body.classList.remove(NO_SCROLL_CLASS)
                if (currentState.originalPaddingRight) {
                    body.style.paddingRight = currentState.originalPaddingRight
                } else {
                    body.style.removeProperty('padding-right')
                }

                // A responsive editor can replace the document under nested locks.
                // Its scroll position must not inherit the previous background's offset.
                if (currentState.scrollOwner?.isConnected &&
                    currentState.scrollOwner === getDocumentScrollOwner() &&
                    Number.isFinite(currentState.scrollY) && window.scrollY !== currentState.scrollY) {
                    window.scrollTo(0, currentState.scrollY)
                }
                currentState.scrollOwner = null
            }

            const scrollLockStateConsistent =
                documentElement.classList.contains(NO_SCROLL_CLASS) ===
                    (currentState.count > 0 || currentState.htmlHadNoScroll) &&
                body.classList.contains(NO_SCROLL_CLASS) ===
                    (currentState.count > 0 || currentState.bodyHadNoScroll)

            onChange?.({
                lockAction: 'released',
                lockCount: currentState.count,
                scrollLockStateConsistent,
            })
        }
    }, [enabled, onChange])
}
